import { useQuery } from "@tanstack/react-query";
import { ClipboardList } from "lucide-react";

import { getApplicationStatus, type ApplicationStatus } from "@/api/admin";
import { Spinner } from "@/components/Spinner";
import { RightPanelDisclosure } from "./RightPanelDisclosure";

const MODE_LABEL: Record<string, string> = {
  E: "E-Verification",
  P: "In-person (SC)",
};

const PENDING_LABEL: Record<NonNullable<ApplicationStatus["pending_action"]>, string> = {
  lock_and_submit: "Student to lock & submit",
  resubmit: "Student to resubmit",
  edit_and_resubmit: "Student to edit & resubmit",
  book_slot: "Student to book slot",
};

function colorChipClasses(color: ApplicationStatus["status_color"]) {
  if (color === "green") {
    return "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200";
  }
  if (color === "orange") {
    return "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200";
  }
  return "bg-slate-100 text-slate-700 dark:bg-surface-border dark:text-slate-200";
}

// Pastel tint applied to the whole panel so status is legible at a glance
// without needing a separate colored dot. `!` overrides the `card` class's
// default surface bg. For unknown/grey we leave the default card bg alone,
// since slate-50 is nearly identical to canvas-raised and would look like a
// no-op.
function panelBgClasses(color: ApplicationStatus["status_color"] | undefined) {
  if (color === "green") {
    return "!bg-emerald-100/70 !border-emerald-300 dark:!bg-emerald-950/40 dark:!border-emerald-800";
  }
  if (color === "orange") {
    return "!bg-amber-100/70 !border-amber-300 dark:!bg-amber-950/40 dark:!border-amber-800";
  }
  return "";
}

export function ApplicationStatusPanel({
  tabId,
  grievanceId,
}: {
  tabId: string;
  grievanceId: number;
}) {
  const q = useQuery({
    queryKey: ["application-status", tabId, grievanceId],
    queryFn: () => getApplicationStatus(tabId, grievanceId),
  });

  const status = q.data;

  return (
    <RightPanelDisclosure
      title="Application Status"
      icon={<ClipboardList size={14} />}
      containerClassName={panelBgClasses(status?.status_color ?? null)}
      defaultOpen
    >
      <div className="px-4 pt-2 pb-3 space-y-1.5 text-sm">
        {q.isLoading ? (
          <Spinner label="Loading status…" />
        ) : q.isError ? (
          <div className="alert-warning text-xs">
            Could not load application status.
            <button
              type="button"
              onClick={() => q.refetch()}
              className="ml-2 underline"
            >
              Retry
            </button>
          </div>
        ) : status ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              {status.verification_mode && MODE_LABEL[status.verification_mode] && (
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-transparent border border-slate-300/70 text-slate-700 dark:border-surface-border dark:text-slate-200">
                  {MODE_LABEL[status.verification_mode]}
                </span>
              )}
              {status.status_color && (
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${colorChipClasses(status.status_color)}`}
                >
                  {status.status_color === "green" ? "Confirmed" : "In progress"}
                </span>
              )}
              {status.pending_action && (
                <span className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">
                  {PENDING_LABEL[status.pending_action]}
                </span>
              )}
            </div>
            <p className="whitespace-pre-wrap leading-relaxed text-sm text-slate-700 dark:text-slate-200">
              {status.status_text || (
                <span className="text-slate-500 italic">Status not available.</span>
              )}
            </p>
          </>
        ) : null}
      </div>
    </RightPanelDisclosure>
  );
}
